import {
  Button,
  Card,
  Flex,
  Link,
  Text,
  Timeline,
  useAppTheme,
} from '@workwhile/ui'
import { DrugScreening } from 'api/typings'
import { RefreshCcw } from 'lucide-react'
import moment from 'moment'
import React, { CSSProperties, useState } from 'react'

const DATE_TIME_SUBMITTED_FORMAT = 'YYYY-MM-DD h:mmA z'

const capitalizeStyle: CSSProperties = { textTransform: 'capitalize' }

type DrugScreeningCardProps = {
  drugScreening: DrugScreening
  sequence: number
  makeReportUrl: (reportId: string) => string | undefined
  userTimeZone: string
  refreshReport?: (reportId: string) => Promise<void>
}

type DrugScreeningEnum = NonNullable<
  NonNullable<DrugScreening['transitions']>[number]['status']
>

const DrugScreeningCard = ({
  drugScreening,
  sequence,
  makeReportUrl,
  userTimeZone,
  refreshReport,
}: DrugScreeningCardProps) => {
  const { colors } = useAppTheme()

  const [refreshFetching, setRefreshFetching] = useState(false)

  const colorMap: Record<DrugScreeningEnum, string> = {
    canceled: colors.neutrals[200],
    clear: colors.success,
    consider: colors.error,
    pending: colors.neutrals[200],
    rejected: colors.error,
    suspended: colors.error,
  }

  const isExpired =
    drugScreening?.expiresAt &&
    moment(drugScreening.expiresAt).isBefore(moment(), 'ms')

  const drugScreenTransitions = drugScreening.transitions?.map((transition) => {
    const color = colorMap[transition.status] || colors.neutrals[200]
    return (
      <>
        {isExpired ? (
          <Timeline.Item key="expired" color={colors.neutrals[200]}>
            <Text color="lightText" fontSize={0}>
              {moment
                .tz(drugScreening.expiresAt, userTimeZone)
                .format(DATE_TIME_SUBMITTED_FORMAT)}
            </Text>
            <Text style={capitalizeStyle} fontSize={2}>
              Expired
            </Text>
          </Timeline.Item>
        ) : null}
        <Timeline.Item key={transition.status} color={color}>
          <Text color="lightText" fontSize={0}>
            {moment
              .tz(transition.occurredAt, userTimeZone)
              .format(DATE_TIME_SUBMITTED_FORMAT)}
          </Text>
          <Text style={capitalizeStyle} fontSize={2}>
            {transition.status}
          </Text>
        </Timeline.Item>
      </>
    )
  })

  const reportUrl = drugScreening.reportId
    ? makeReportUrl(drugScreening.reportId)
    : undefined

  const refreshVisible =
    Boolean(drugScreening.reportId) && refreshReport !== undefined

  const handleClickRefresh = async () => {
    if (!drugScreening.reportId) {
      return
    }
    if (refreshReport === undefined) {
      return
    }

    setRefreshFetching(true)
    try {
      await refreshReport(drugScreening.reportId)
    } catch (error) {
      console.error('ERROR OCCURED REFRESHING DRUG SCREENING STATUS: ', error)
    }

    setRefreshFetching(false)
    // NOTE: If the caller wants data refreshed as clean-up, it can be done
    //       within `refreshReport()`
  }

  return (
    <Card shadowless mb={2}>
      <Flex justifyContent="space-between">
        <Text>
          Drug Screening #{sequence}{' '}
          {reportUrl ? (
            <Link
              to={reportUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginLeft: '0.5rem' }}
              color="primary"
            >
              (View report)
            </Link>
          ) : null}
        </Text>
        {refreshVisible ? (
          <Button
            kind="small"
            onClick={handleClickRefresh}
            style={{ padding: 4 }}
            variant="text"
            disabled={refreshFetching}
          >
            <RefreshCcw size={14} />
          </Button>
        ) : null}
      </Flex>
      <Flex align="center">
        <Text fontSize={3} style={capitalizeStyle} mt={1}>
          {isExpired ? 'Expired' : drugScreening.status}
        </Text>
      </Flex>
      <Text mt={2} color="lightText">
        History
      </Text>
      {drugScreenTransitions ? (
        <Timeline mt={2}>{drugScreenTransitions}</Timeline>
      ) : (
        <Text>No history found</Text>
      )}
    </Card>
  )
}

type UserDrugScreeningsProps = {
  drugScreenings?: DrugScreening[]
  makeReportUrl: (reportId: string) => string | undefined
  userTimeZone: string
  refreshReport?: (reportId: string) => Promise<void>
}

export const UserDrugScreenings = ({
  drugScreenings,
  makeReportUrl,
  userTimeZone,
  refreshReport,
}: UserDrugScreeningsProps) => {
  if (!Array.isArray(drugScreenings) || !drugScreenings.length) {
    return (
      <>
        <Text mb={2}>Drug Screenings</Text>
        <Text>No drug screenings found for this user</Text>
      </>
    )
  }

  const drugScreeningsCards = drugScreenings.map((drugScreening, index) => (
    <DrugScreeningCard
      key={drugScreening.reportId}
      drugScreening={drugScreening}
      sequence={index + 1}
      makeReportUrl={makeReportUrl}
      userTimeZone={userTimeZone}
      refreshReport={refreshReport}
    />
  ))

  return (
    <>
      <Text mb={2}>Drug Screenings</Text>
      {drugScreeningsCards}
    </>
  )
}
